import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, AlertCircle, Loader2, Layers } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Module-level dedup: survives StrictMode double-invocation and any re-renders.
// Map<token, Promise> — if the same token is being verified concurrently, all
// callers await the same in-flight request (backend is called exactly once per token).
const pendingVerifications = new Map();

function verifyToken(token) {
  if (pendingVerifications.has(token)) return pendingVerifications.get(token);
  const p = (async () => {
    try {
      const res = await axios.post(
        `${API_URL}/api/auth/verify-email`,
        { token },
        { headers: { 'Content-Type': 'application/json' } },
      );
      const data = res.data || {};
      if (!data?.access_token || !data?.user) {
        throw new Error('Verification succeeded but the server did not return a session.');
      }
      return data;
    } catch (e) {
      const apiDetail = e?.response?.data?.detail;
      const msg =
        typeof apiDetail === 'string' ? apiDetail : e?.message || 'Could not verify this link.';
      throw new Error(msg);
    }
  })();
  pendingVerifications.set(token, p);
  return p;
}

/**
 * Landing page for the link in the verification email.
 * Reads `?token=…`, POSTs to `/api/auth/verify-email`, then either:
 *  - auto-logs-in the user (stores JWT + user) and hard-navigates to /paper-trading
 *  - shows a friendly error with a "Back to sign in" affordance
 */
export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [status, setStatus] = useState('verifying');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMsg('This link is missing a verification token. Request a new one from the login page.');
      return;
    }
    let cancelled = false;
    verifyToken(token)
      .then((data) => {
        if (cancelled) return;
        localStorage.setItem('tp360_token', data.access_token);
        localStorage.setItem('tp360_user', JSON.stringify(data.user));
        setStatus('success');
        toast.success(`Email verified — welcome, ${data.user.name}!`);
        setTimeout(() => {
          window.location.assign('/paper-trading');
        }, 1500);
      })
      .catch((e) => {
        if (cancelled) return;
        setStatus('error');
        setErrorMsg(e.message || 'Could not verify this link.');
      });
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div className="min-h-screen bg-[#0a0a0c] flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 bg-[#111113]/95 border-white/5 text-center">
        <div className="flex flex-col items-center mb-6">
          <div className="w-14 h-14 bg-gradient-to-br from-primary to-primary/70 rounded-xl flex items-center justify-center shadow-lg shadow-primary/20 mb-4">
            <Layers className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-black tracking-tight text-foreground">Trading Pilot</h1>
          <p className="text-primary font-bold">360</p>
        </div>

        {status === 'verifying' && (
          <div className="py-4" data-testid="verify-loading">
            <Loader2 className="w-10 h-10 text-primary mx-auto mb-4 animate-spin" />
            <p className="text-base font-semibold text-foreground mb-1">Verifying your email…</p>
            <p className="text-sm text-muted-foreground">This only takes a second.</p>
          </div>
        )}

        {status === 'success' && (
          <div className="py-4" data-testid="verify-success">
            <CheckCircle2 className="w-12 h-12 text-success mx-auto mb-4" />
            <p className="text-base font-semibold text-foreground mb-1">Email verified</p>
            <p className="text-sm text-muted-foreground">Taking you to your dashboard…</p>
          </div>
        )}

        {status === 'error' && (
          <div className="py-4" data-testid="verify-error">
            <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <p className="text-base font-semibold text-foreground mb-2">Verification failed</p>
            <p className="text-sm text-muted-foreground mb-6">{errorMsg}</p>
            <Button asChild className="w-full">
              <Link to="/?login=1" data-testid="verify-back-to-login">Back to sign in</Link>
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
